
import Vue from 'vue';
import { mapGetters, mapActions, mapState } from 'vuex';
import mixins from 'vue-typed-mixins';

import { formatNumber } from '@/i18n';

import ClinicalTrainingEvent from '@/store/modules/clinical-training-events/clinical-training-event';
import { ClinicalTrainingEventStatus } from '@/store/modules/clinical-training-events/clinical-training-event';
import { ClinicalTrainingEventsActions } from '@/store/modules/clinical-training-events/types';

import CheckboxControl from '@/components/controls/checkbox.vue';
import tableViewMixin from '@/components/mixins/table-view';
import NotificationBanner from '@/components/blocks/notification-banner.vue';
import TableListSkeleton from '@/components/skeletons/table-list.vue';
import Dropdown, { DropdownItems } from '@/components/controls/dropdown.vue';
import PaginationControl from '@/components/controls/pagination.vue';
import ClinicalTrainingEventsFilter from '@/components/forms/clinical-training-events-filter.vue';
import ClinicalTrainingEventParticipantsModal from '@/components/layout/clinical-training-event-participants-modal.vue';

export default mixins(tableViewMixin).extend({
  name: 'ClinicalTrainingEventsView',
  components: {
    NotificationBanner,
    TableListSkeleton,
    Dropdown,
    PaginationControl,
    ClinicalTrainingEventsFilter,
    CheckboxControl,
  },
  data() {
    return {
      selectedItems: [],
      ClinicalTrainingEventStatus,
    };
  },
  computed: {
    ...mapGetters('user/profile', ['isSuperAdmin', 'isAdmin']),
    ...mapState('clinicalTrainingEvents', {
      events: 'list',
      pagination: 'listPagination',
    }),
    ...mapGetters('clinicalTrainingEvents', {
      isLoading: 'listIsLoading',
      isLoaded: 'listIsLoaded',
      isEmpty: 'listIsEmpty',
    }),
    areAllItemsSelected(): boolean {
      return this.selectedItems.length === this.events.length;
    },
  },
  methods: {
    formatNumber,
    formatDate(dateString: string) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }) + ' EST';
    },
    ...mapActions('clinicalTrainingEvents', {
      restoreAction: ClinicalTrainingEventsActions.RESTORE,
      removeEvent: ClinicalTrainingEventsActions.DELETE,
    }),
    dropdownItems(event: ClinicalTrainingEvent) {
      const items: DropdownItems = {
        edit: {
          label: this.$t('Edit'),
          icon: 'pencil',
        },
      };

      if (event.status === this.ClinicalTrainingEventStatus.Inactive) {
        items.restore = {
          label: this.$t('Restore'),
          icon: 'restore',
        };
      } else {
        items.delete = {
          label: this.$t('Delete'),
          icon: 'delete',
        };
      }

      return items;
    },
    handleClick(event: ClinicalTrainingEvent, action: string) {
      if ('edit' === action) {
        this.$router.push({
          name: 'edit-clinical-training-event',
          params: { id: String(event.id) },
        });
      }

      if ('restore' === action) {
        const answer = window.confirm(this.$t('Do you really want to restore this clinical training event?'));

        if (answer) {
          this.restoreAction({ id: event.id });
        }
      }

      if ('delete' === action) {
        const answer = window.confirm(this.$t('Do you really want to delete this clinical training event?'));

        if (answer) {
          this.removeEvent({ id: event.id });
        }
      }
    },
    isSelected(event: ClinicalTrainingEvent): boolean {
      return this.selectedItems.indexOf(event.id) !== -1;
    },
    selectItem(event: ClinicalTrainingEvent) {
      if (this.isSelected(event)) {
        this.selectedItems = this.selectedItems.filter((item) => item !== event.id);
        return;
      }

      this.selectedItems.push(event.id);
    },
    selectAll() {
      if (this.areAllItemsSelected) {
        this.selectedItems = [];
      } else {
        this.selectedItems = this.events.map((event: ClinicalTrainingEvent) => event.id);
      }
    },
    openParticipantsModal(event: ClinicalTrainingEvent) {
      this.$modal.show(ClinicalTrainingEventParticipantsModal, { event }, { name: 'clinical-training-event-participants' });
    },
  },
});
